import type { Collections, GoogleItem, SemiLAM2, SKU } from "./types";
import eventHandlers from "./eventHandlers";

export function getCollectionNameForEvent(event: string) {
  switch (event) {
    case "view_item":
    case "view_item_list":
    case "add_to_wishlist":
    case "remove_from_wishlist":
      return "products";
    case "view_cart":
      return "cart";
    default:
      return "ERROR: Invalid event no collection found";
  }
}

// returns price value from a sku, depending upon isSale
export function getPriceFromSku<T extends SKU>(sku: T) {
  let value: number | string = 0;
  if (sku.price.isSale === true) {
    value = sku.price.sale;
  } else {
    value = sku.price.regular;
  }
  return typeof value === "string"
    ? Number(Number.parseFloat(value).toFixed(2))
    : value;
}

export function getDiscountFromSku<T extends SKU>(sku: T) {
  if (!sku.price.isSale) {
    return 0;
  }
  return Number(sku.price.regular) - Number(sku.price.sale);
}

export function getSingleProductAndSku<T extends Collections["products"]>(
  collection: T
) {
  const productId = Object.keys(collection)[0];
  // Get the lowest price sku if there are multiple skus (PDP scenario)
  const skuId = Object.values(collection[productId].skus)
    .map((sku) => [sku.skuId, getPriceFromSku(sku)])
    .sort((a, b) => Number(a[1]) - Number(b[1]))[0][0];

  return {
    productId,
    skuId,
  };
}

export function verifySingleProductAndSku<T extends Collections["products"]>(
  collection: T,
  msg: string
) {
  if (collection === undefined || Object.keys(collection).length === 0) {
    throw new Error("Missing/Invalid product passed to " + msg);
  }
  const productIds = Object.keys(collection);
  if (productIds.length > 1) {
    throw new Error("More than one product " + msg);
  }

  const skus = Object.keys(collection[productIds[0]].skus);
  if (skus.length > 1) {
    throw new Error("More than one sku " + msg + "!");
  }

  return {
    productId: productIds[0],
    skuId: skus[0],
  };
}

// returns items per sku, ingests collections products
export function collectionListItemToGoogleSku<
  T extends Collections[keyof Collections],
>(collection: T): GoogleItem[] {
  return Object.values(collection).reduce<GoogleItem[]>((itemsArray, product) => {
    Object.values(product.skus).forEach((sku) => {
      const discount = sku.price.isSale ? getDiscountFromSku(sku) : undefined;

      itemsArray.push({
        item_id: product.productId,
        item_name: product.unifiedId,
        item_category: product.categoryId,
        quantity: sku.quantity,
        price: getPriceFromSku(sku),
        item_price_type: sku.price.isSale ? "markdown" : "regular",
        item_variant: sku.skuId.length > 0 ? sku.skuId : "not available",
        item_stock_status:
          sku.stockStatus === "oos" ? "out-of-stock" : "in-stock",
        discount,
        // item_product_id: product.productId,
      });
    });
    return itemsArray;
  }, []);
}

// returns items per product, ingests collections products
export function collectionListItemToGoogleProduct<
  T extends Collections[keyof Collections],
>(collection: T) {
  return Object.values(collection).map((product) => {
    // Get the first SKU from the product
    const firstSku = Object.values(product.skus)[0];
    const discount =
      firstSku && firstSku.price.isSale
        ? getDiscountFromSku(firstSku)
        : undefined;

    const item = {
      item_id: product.productId,
      item_name: product.unifiedId,
      item_category: product.categoryId,
      quantity: 1, // Fixed quantity
      price: firstSku ? getPriceFromSku(firstSku) : 0, // Price from the first SKU, or 0 if no SKUs
      item_variant:
        firstSku?.skuId?.length > 0 ? firstSku.skuId : "not available", // Use the first SKU ID or "not available"
      item_price_type: firstSku?.price?.isSale ? "markdown" : "regular", // Use the first SKU price type or "regular"
      item_stock_status:
        firstSku?.stockStatus === "oos" ? "out-of-stock" : "in-stock",
      discount,
    };

    return item;
  });
}

export function extractGlobalsFromLam<T extends { data?: Record<string, any> }>(
  data: SemiLAM2,
  result: T
) {
  const resolution =
    data.context["window"].screen.height +
    "x" +
    data.context["window"].screen.width;
  result.data.screen_resolution = resolution;
  result.data.language = data.context["navigator"].language;
  result.data.user_agent = data.context["navigator"].userAgent;
  //document
  result.data.page_location = data.context["document"].location.href;
  result.data.page_referrer = data.context["document"].referrer;
  result.data.page_title = data.context["document"].title;
  // app
  result.data.locale = data.context.app["locale"];
  result.data.currency = data.context.app.currency;
  result.data.platform_property = data.context.app.property;
  result.data.data_model_version = "1.2.0";
  // session
  result.data.campaign_id = data.payload["user"].session.cid;
  result.data.qm_session_id = data.payload["user"].session.qmId;
  result.data.digital_client_id =
    data.payload["user"].session.digital_client_id;
  result.data.site_variation = data.payload["user"].session.site_variation;
  // consent
  result.data.consent_id = data.payload["user"].consent.otId;
  result.data.consent_preference = data.payload["user"].consent.value;
  result.data.consent_groups = data.payload["user"].consent.value;
  //profile
  result.data.user_auth_state = data.payload["user"].profile.authState;
  result.data.hashed_email = data.payload["user"].profile.hashedEmail;
  result.data.okta_id = data.payload["user"].profile.oktaId;
  result.data.user_id = data.payload["user"].profile.hashedEmail;
  // event
  result.data.event_id = data.payload.event.eventId;
}

export class GAHandlerNotFoundError extends Error {
  constructor(event: string) {
    super(`GA Handler for ${event} does not exist`);
    this.name = "GAHandlerNotFoundError";
  }
}

export function gaEvent(
  event: string,
  data: SemiLAM2,
  gtag: (
    type: "event",
    eventName: string,
    eventParams: Record<string, any>
  ) => void
) {
  if (event === undefined || event === null) {
    return "ERROR: Missing event";
  }
  if (data === undefined || data === null) {
    return "ERROR: Missing data";
  }

  const handlerObject = eventHandlers[event];

  if (handlerObject === undefined) {
    throw new GAHandlerNotFoundError(event);
  }

  const result = handlerObject.handler(handlerObject.name, data);
  extractGlobalsFromLam(data, result);
  gtag("event", result.event, result.data);
}
